import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Maximize2, Minimize2, Minus, Sparkles } from 'lucide-react';
import { useMediaQuery } from '../lib/useMediaQuery';
import AgentCard, { type AgentCardHandle } from './AgentCard';
import DocumentViewer from './DocumentViewer';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { AgentMessage } from '../lib/types';

// How the agent card shows: docked beside the content (a third of the
// width, wide screens only), minimised to a button in the corner, maximised
// (half the width beside the content on a wide screen, the whole pane on a narrow
// one), or reading — the chat in the left third and a document it wrote open in
// the rest, covering the page.
export type AgentCardMode = 'docked' | 'minimised' | 'maximised' | 'reading';

/** Card actions, called from click handlers so each click acts exactly once. */
export interface AgentCardActions {
  /** `beside` keeps the page in view where there is room; `ask` is sent first if given. */
  startChat: (agent?: string, opts?: { ask?: string; beside?: boolean }) => void;
  openChat: (id: string) => void;
  /** Docked where there is room, otherwise minimised. Every menu item and top-bar link does this. */
  resetCard: () => void;
}

interface AgentCardState extends AgentCardActions {
  shown: AgentCardMode;
  wide: boolean;
  sectionId: string | null;
  doc: AgentMessage | null;
  setMode: (mode: Exclude<AgentCardMode, 'reading'>) => void;
  openDocument: (doc: AgentMessage) => void;
  closeDocument: () => void;
  attachCard: (card: AgentCardHandle | null) => void;
}

const AgentCardContext = createContext<AgentCardState | null>(null);

export function useAgentCard(): AgentCardState {
  const ctx = useContext(AgentCardContext);
  if (!ctx) throw new Error('useAgentCard must be used inside AgentCardProvider');
  return ctx;
}

export function AgentCardProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const wide = useMediaQuery('(min-width: 896px)');
  const parts = location.pathname.split('/').filter(Boolean);
  const sectionId = parts[0] === 'workspace' && parts[1] ? parts[1] : null;

  const [mode, setModeState] = useState<AgentCardMode>(wide ? 'docked' : 'minimised');
  // The document open beside the chat, and the mode to return to when it closes.
  const [doc, setDoc] = useState<AgentMessage | null>(null);
  const [beforeDoc, setBeforeDoc] = useState<AgentCardMode>(mode);
  const cardRef = useRef<AgentCardHandle | null>(null);
  // The card mounts once the workspace's agents have loaded; an action asked for before then
  // waits for it.
  const pending = useRef<((card: AgentCardHandle) => void) | null>(null);
  const withCard = useCallback((action: (card: AgentCardHandle) => void) => {
    if (cardRef.current) action(cardRef.current);
    else pending.current = action;
  }, []);
  const attachCard = useCallback((card: AgentCardHandle | null) => {
    cardRef.current = card;
    const action = pending.current;
    if (card && action) {
      pending.current = null;
      action(card);
    }
  }, []);

  // Any other change of mode closes the document.
  const setMode = useCallback((next: Exclude<AgentCardMode, 'reading'>) => {
    setDoc(null);
    setModeState(next);
  }, []);
  const openDocument = useCallback((d: AgentMessage) => {
    setDoc(current => {
      if (!current) setBeforeDoc(mode);
      return d;
    });
  }, [mode]);
  const closeDocument = useCallback(() => {
    setDoc(null);
    setModeState(beforeDoc);
  }, [beforeDoc]);

  const startChat = useCallback<AgentCardActions['startChat']>((agent, opts = {}) => {
    setMode(opts.beside && wide ? 'docked' : 'maximised');
    withCard(card => card.startChat(agent, opts.ask));
  }, [setMode, wide, withCard]);
  const openChat = useCallback((id: string) => {
    setMode('maximised');
    withCard(card => card.openChat(id));
  }, [setMode, withCard]);
  const resetCard = useCallback(() => setMode(wide ? 'docked' : 'minimised'), [setMode, wide]);

  // ?chat=<id> opens that conversation, as the link Navaris gives an AI assistant does.
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('chat');
    if (!id) return;
    openChat(id);
    params.delete('chat');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [location.search, location.pathname, navigate, openChat]);

  // Narrowing the window while docked leaves no room beside the content.
  const shown: AgentCardMode = doc ? 'reading' : !wide && mode === 'docked' ? 'minimised' : mode;

  const value = useMemo(() => ({
    shown, wide, sectionId, doc, setMode, openDocument, closeDocument, attachCard,
    startChat, openChat, resetCard,
  }), [shown, wide, sectionId, doc, setMode, openDocument, closeDocument, attachCard, startChat, openChat, resetCard]);

  return <AgentCardContext.Provider value={value}>{children}</AgentCardContext.Provider>;
}

function WindowButton({ onClick, label, children }: { onClick: () => void; label: string; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="w-7 h-7 grid place-items-center rounded-md text-coolslate hover:text-brand-dark hover:bg-offwhite transition-colors"
    >
      {children}
    </button>
  );
}

const SIDE_SHADOW = 'shadow-[4px_0_12px_-6px_rgba(16,40,48,0.25)]';

/** The content pane's columns beside the card: a third for it docked, half for it
 * maximised on a wide screen, the whole width otherwise. */
export function dockColumns(shown: AgentCardMode, wide: boolean): string {
  if (shown === 'docked') return 'minmax(0,1fr) minmax(0,2fr)';
  if (shown === 'maximised' && wide) return 'minmax(0,1fr) minmax(0,1fr)';
  return 'minmax(0,1fr)';
}

/** The card in whichever mode it is in, plus the corner button when minimised. */
export default function AgentDock() {
  const { shown, setMode, wide, sectionId, doc, openDocument, closeDocument, attachCard } = useAgentCard();
  const { agents } = useWorkspace();
  const author = doc?.skill ? agents.find(a => a.key === doc.skill)?.name : undefined;
  // Beside the content: docked, or maximised on a wide screen. It sits a little above
  // the content pane, edge to edge, with a shadow on its right.
  const beside = shown === 'docked' || (shown === 'maximised' && wide);
  return (
    <>
      <aside
        className={
          beside
            // Sticky so it stays in view as the content scrolls: the viewport less the 48px top bar.
            ? `sticky top-0 z-10 h-[calc(100vh-48px)] ${SIDE_SHADOW}`
            : shown === 'maximised'
              ? 'fixed inset-x-0 top-12 bottom-0 z-20'
              : shown === 'reading' && wide
                ? `fixed left-0 top-12 bottom-0 w-1/3 z-20 ${SIDE_SHADOW}`
                : 'hidden'
        }
      >
        <div className="h-full bg-white">
          <AgentCard
            ref={attachCard}
            sectionId={sectionId}
            onOpenDocument={openDocument}
            openDocumentId={doc?.id ?? null}
            // The agent moved the person to a page: make room for it.
            onNavigate={() => shown === 'maximised' && setMode(wide ? 'docked' : 'minimised')}
            actions={
              <>
                {shown === 'maximised' && !doc ? (
                  wide && (
                    <WindowButton onClick={() => setMode('docked')} label="Restore">
                      <Minimize2 className="w-4 h-4" />
                    </WindowButton>
                  )
                ) : (
                  <WindowButton onClick={() => setMode('maximised')} label="Maximise">
                    <Maximize2 className="w-4 h-4" />
                  </WindowButton>
                )}
                <WindowButton onClick={() => setMode('minimised')} label="Minimise">
                  <Minus className="w-4 h-4" />
                </WindowButton>
              </>
            }
          />
        </div>
      </aside>

      {doc && (
        <div
          className={`fixed top-12 bottom-0 right-0 z-20 bg-offwhite ${
            wide ? 'left-1/3 p-6' : 'left-0 p-3'
          }`}
        >
          <DocumentViewer doc={doc} author={author} onClose={closeDocument} />
        </div>
      )}

      {shown === 'minimised' && (
        <button
          type="button"
          onClick={() => setMode(wide ? 'docked' : 'maximised')}
          aria-label="Ask Navaris"
          title="Ask Navaris"
          className="fixed bottom-6 left-6 z-20 w-14 h-14 rounded-full grid place-items-center bg-brand text-white shadow-lg hover:bg-brand-dark transition-colors"
        >
          <Sparkles className="w-6 h-6" />
        </button>
      )}
    </>
  );
}
